import React, { useState } from 'react';
import cn from 'classnames';
import * as yup from 'yup';
import { FormProvider, useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers';
import { useMutation } from '@apollo/client';
import { Typography, Button, Input, Collapse } from 'antd';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
// Constants
import { POST_DELIVERABLE_LINK_TYPES } from 'constants/deliverables';
// Api
import { SUBMIT_CAMPAIGN_OFFER_RESULTS_V2 } from 'api/campaignV2/mutations';
import { GET_CAMPAIGN_OFFERS } from 'api/campaignV2/queries';
// Types
import {
  GetCampaignOffers_getCampaignOffers_entities,
  GetCampaignOffers_getCampaignOffers_entities_deliverablesV2,
} from 'api/campaignV2/types/GetCampaignOffers';
import {
  SubmitCampaignOfferResultsV2,
  SubmitCampaignOfferResultsV2Variables,
} from 'api/campaignV2/types/SubmitCampaignOfferResultsV2';
import {
  CampaignTaskType,
  CbDeliverableV2Status,
  SubmitCampaignOfferResultsInput,
} from 'api/graphql-global-types';
// Hooks
import { useUploadCampaignFiles } from 'hooks';
// Components
import UploadTimelineFilesInput from '../UploadTimelineFilesInput/UploadTimelineFilesInput';
// Ui
import { successNotification } from 'ui/Notification';
// Styles
import styles from './SubmitPostForm.module.scss';

const { Text } = Typography;
const { Panel } = Collapse;

type FileWithPreview = File & {
  preview: string;
};

type PostFormData = {
  type: CampaignTaskType;
  offerId: string;
  files: FileWithPreview[];
  link: string;
};

const validationSchema = yup.object().shape({
  type: yup.string().required('Type is required'),
  link: yup.string().when('type', {
    is: (type: string) =>
      POST_DELIVERABLE_LINK_TYPES.includes(type as CampaignTaskType),
    then: (schema) =>
      schema
        .required('Please upload valid url link.')
        .min(1, 'Url link cannot be empty')
        .url('Please upload valid url link.'),
    otherwise: (schema) => schema.optional(),
  }),
  files: yup.array().when('type', {
    is: (type: string) =>
      !POST_DELIVERABLE_LINK_TYPES.includes(type as CampaignTaskType),
    then: (schema) =>
      schema.min(1, 'Please upload the screenshots.').of(
        yup
          .mixed()
          .test(
            'is-file',
            'Each item must be a File',
            (value) => value instanceof File
          )
          .required('File is required')
      ),
    otherwise: (schema) => schema.optional(),
  }),
});

type SubmitPostFormProps = {
  offer?: GetCampaignOffers_getCampaignOffers_entities | null;
  deliverable: GetCampaignOffers_getCampaignOffers_entities_deliverablesV2;
  className?: string;
  hasPendingRequest?: (deliverableId: string) => boolean;
  onRequestExtension?: (
    deliverable: GetCampaignOffers_getCampaignOffers_entities_deliverablesV2
  ) => void;
  onReviewExtension?: (
    deliverable: GetCampaignOffers_getCampaignOffers_entities_deliverablesV2
  ) => void;
};

const SubmitPostForm: React.FC<SubmitPostFormProps> = ({
  offer,
  deliverable,
  className,
  hasPendingRequest,
  onRequestExtension,
  onReviewExtension,
}) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [isUploading, setIsUploading] = useState<boolean>(false);

  const { createCampaignDeliverableFiles, uploadDeliverables } =
    useUploadCampaignFiles();

  const [submitCampaignOfferResultsV2] = useMutation<
    SubmitCampaignOfferResultsV2,
    SubmitCampaignOfferResultsV2Variables
  >(SUBMIT_CAMPAIGN_OFFER_RESULTS_V2);

  const methods = useForm<PostFormData>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      type: deliverable?.task?.type,
      files: [],
      link: '',
    },
    mode: 'onChange',
    reValidateMode: 'onChange',
    shouldUnregister: false,
  });

  // Watch form values to check if content is provided
  const watchedValues = methods.watch();
  const isLinkType = POST_DELIVERABLE_LINK_TYPES.includes(
    deliverable?.task?.type ?? ('' as CampaignTaskType)
  );

  // Check if content is provided based on deliverable type
  const hasContent = isLinkType
    ? watchedValues.link && watchedValues.link.trim().length > 0
    : watchedValues.files && watchedValues.files.length > 0;

  const isSubmitDisabled = !isExpanded || !hasContent;

  const handleToggleExpand = () => {
    setIsExpanded((prev) => !prev);
  };

  const handleSubmit = () => {
    if (!isExpanded) {
      handleToggleExpand();
    } else {
      methods.handleSubmit(handleFormSubmit)();
    }
  };

  const handleFormSubmit = async (data: PostFormData) => {
    setIsUploading(true);
    const submissions: SubmitCampaignOfferResultsInput['submissions'] = [];

    try {
      if (data?.files?.length > 0) {
        const { data: filesData } = await createCampaignDeliverableFiles({
          variables: {
            input: {
              files: data.files.map((file) => ({
                name: file.name,
              })),
            },
          },
        });
        const presignedFiles = filesData?.createCampaignDeliverableFiles || [];

        const uploadResults = await uploadDeliverables(
          presignedFiles,
          data.files
        );
        const submitInput = uploadResults.map((result) => ({
          type: data.type,
          taskId: deliverable?.task?.id,
          imageKey: result.key,
        }));
        submissions.push(...submitInput);
      }

      if (data.link) {
        submissions.push({
          type: data.type,
          taskId: deliverable?.task?.id,
          link: data.link,
        });
      }

      await submitCampaignOfferResultsV2({
        variables: {
          input: {
            offerId: offer?.id ?? '',
            submissions,
          },
        },
        refetchQueries: [
          {
            query: GET_CAMPAIGN_OFFERS,
            variables: {
              input: {
                campaignId: offer?.campaign.id ?? '',
              },
            },
          },
        ],
      });
      successNotification('Post submitted successfully!');
      setIsExpanded(false);
    } catch (error) {
      console.error('Error submitting post:', error);
    } finally {
      setIsUploading(false);
    }
  };

  const renderFormContent = () => (
    <div className={styles.contentContainer}>
      <div className={styles.successMessage}>
        <div className={styles.successContent}>
          <div>
            <Text>Approved Content</Text>
            <Text>
              View approved content and post it based on the below requirements.
            </Text>
          </div>
        </div>
      </div>
      {POST_DELIVERABLE_LINK_TYPES.includes(
        deliverable?.task?.type ?? ('' as CampaignTaskType)
      ) ? (
        <>
          <Text>Add Social Post Link</Text>
          <Controller
            name="link"
            control={methods.control}
            render={({ onChange, onBlur, value, name, ref }) => (
              <Input
                name={name}
                ref={ref}
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                placeholder="URL"
              />
            )}
          />
        </>
      ) : (
        <div className={styles.uploadContainer}>
          <div className={styles.uploadTitle}>
            <Text>Upload Screenshots or Post Insights</Text>
            <Text>
              Make sure to include screenshots of all insights provided for your
              post.
            </Text>
          </div>

          <UploadTimelineFilesInput
            fieldName="files"
            inputTitle={'Drop files here or click to upload'}
            inputDescription={'Support for images and videos up to 5GB'}
            tagIcon="image"
            acceptFileTypes={{
              'image/png': ['.png'],
              'image/jpeg': ['.jpg', '.jpeg'],
              'video/mp4': ['.mp4'],
            }}
            maxFiles={10}
          />
        </div>
      )}
    </div>
  );

  return (
    <div className={cn(styles.root, className)}>
      <FormProvider {...methods}>
        <form
          className={cn(styles.form, className)}
          onSubmit={methods.handleSubmit(handleFormSubmit)}
        >
          <Collapse
            activeKey={isExpanded ? ['1'] : []}
            onChange={() => setIsExpanded(!isExpanded)}
            ghost
            className={styles.collapse}
          >
            <Panel
              header={
                <div className={styles.panelHeader}>
                  <Text strong>Submit</Text>
                </div>
              }
              key="1"
              extra={
                <Button
                  type="text"
                  icon={isExpanded ? <UpOutlined /> : <DownOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleExpand();
                  }}
                />
              }
            >
              {renderFormContent()}
            </Panel>
          </Collapse>

          <div className={styles.buttonsContainer}>
            <Button
              type="primary"
              className={styles.submitButton}
              onClick={handleSubmit}
              loading={isUploading}
              disabled={isSubmitDisabled}
            >
              {isUploading
                ? 'Submitting...'
                : isExpanded
                ? 'Submit Post'
                : 'Submit Post'}
            </Button>

            {/* Extension Request Buttons */}
            {deliverable?.status !== CbDeliverableV2Status.ON_BRAND_REVIEW &&
              deliverable?.status !== CbDeliverableV2Status.PUBLISHED &&
              hasPendingRequest &&
              onRequestExtension &&
              onReviewExtension && (
                <div className={styles.extensionButtons}>
                  {hasPendingRequest(deliverable.id) ? (
                    <Button
                      type="primary"
                      onClick={() => onReviewExtension(deliverable)}
                    >
                      Review Extension
                    </Button>
                  ) : (
                    <Button
                      type="primary"
                      onClick={() => onRequestExtension(deliverable)}
                    >
                      Request Extension
                    </Button>
                  )}
                </div>
              )}
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

export default SubmitPostForm;
